import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  makeStyles,
  Paper,
  Button,
  Grid,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@material-ui/core";
import { Check, Close, AccessTime, GetApp } from "@material-ui/icons";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { toast } from "react-toastify";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  card: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  media: {
    height: 250,
    backgroundSize: "contain",
    backgroundColor: "#f5f5f5"
  },
  content: {
    flexGrow: 1,
  },
  chip: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(2),
  },
  modalImage: {
    width: "100%",
    height: "auto",
  }
}));

const AiApprovals = () => {
  const classes = useStyles();
  const history = useHistory();
  const [medias, setMedias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const fetchMedias = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai-media");
      setMedias(data);
    } catch (err) {
      toast.error("Error al cargar aprobaciones.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchMedias();
    
    // Intervalo de auto-refresh cada 30 segundos si hay algo PENDING o no hay nada
    const interval = setInterval(() => {
        fetchMedias();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchMedias]);

  const handleAction = async (id, action) => {
    try {
      const status = action === "approve" ? "APPROVED" : "REJECTED";
      await api.put(`/ai-media/${id}/status`, { status });
      toast.success(action === "approve" ? "Imagen aprobada." : "Imagen rechazada. Se generará otra.");
      fetchMedias();
    } catch (err) {
      toast.error("Error al procesar la acción.");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING": return "default";
      case "APPROVED": return "primary";
      case "REJECTED": return "secondary";
      case "PROCESSING": return "secondary"; // Will render orange via style
      default: return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "PENDING": return "Pendiente de Aprobación";
      case "APPROVED": return "Aprobado";
      case "REJECTED": return "Rechazado";
      case "PROCESSING": return "Generando Imagen...";
      default: return status;
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Aprobaciones IA</Title>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {loading && medias.length === 0 ? (
            <Typography>Cargando...</Typography>
        ) : medias.length === 0 ? (
            <Typography>No hay imágenes generadas por IA en este momento.</Typography>
        ) : (
          <Grid container spacing={3}>
            {medias.map((media) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={media.id}>
                <Card className={classes.card}>
                  {media.status === "PROCESSING" ? (
                     <div style={{ height: 250, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f5f5f5" }}>
                         <Typography color="textSecondary">⏳ Generando en la nube...</Typography>
                     </div>
                  ) : (
                    <CardMedia
                        className={classes.media}
                        image={`${process.env.REACT_APP_BACKEND_URL}/public/company${media.companyId}/campaigns/${media.mediaUrl}`}
                        title="Imagen generada"
                        onClick={() => setPreviewImage(`${process.env.REACT_APP_BACKEND_URL}/public/company${media.companyId}/campaigns/${media.mediaUrl}`)}
                        style={{ cursor: "pointer" }}
                    />
                  )}
                  <CardContent className={classes.content}>
                    <Typography variant="h6" gutterBottom>
                      {media.targetType === "CAMPAIGN" ? "Campaña M." : "Grupo S."} #{media.targetId}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Prompt: {media.promptUsed.substring(0, 80)}...
                    </Typography>
                    <Chip 
                        label={getStatusLabel(media.status)} 
                        color={getStatusColor(media.status)}
                        style={media.status === "PROCESSING" ? { backgroundColor: "#ff9800", color: "#fff" } : {}}
                        className={classes.chip}
                        size="small"
                        icon={media.status === "PENDING" ? <AccessTime /> : undefined}
                    />
                    <Typography variant="caption" display="block">
                      {moment(media.createdAt).format("DD/MM/YYYY HH:mm")}
                    </Typography>
                  </CardContent>
                  <CardActions className={classes.actions}>
                    {media.status === "PENDING" && (
                      <>
                        <Button
                          size="small"
                          color="secondary"
                          variant="outlined"
                          startIcon={<Close />}
                          onClick={() => handleAction(media.id, "reject")}
                        >
                          Rechazar
                        </Button>
                        <Button
                          size="small"
                          color="primary"
                          variant="contained"
                          startIcon={<Check />}
                          onClick={() => handleAction(media.id, "approve")}
                        >
                          Aprobar
                        </Button>
                      </>
                    )}
                    {media.status !== "PROCESSING" && (
                        <Button
                          size="small"
                          color="default"
                          startIcon={<GetApp />}
                          onClick={() => {
                            const url = `${process.env.REACT_APP_BACKEND_URL}/public/company${media.companyId}/campaigns/${media.mediaUrl}`;
                            const link = document.createElement("a");
                            link.href = url;
                            link.download = `ia_${media.id}.jpg`;
                            link.target = "_blank";
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          Descargar
                        </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Modal Preview */}
      <Dialog open={!!previewImage} onClose={() => setPreviewImage(null)} maxWidth="lg">
          <DialogTitle>Vista Previa</DialogTitle>
          <DialogContent>
              <img src={previewImage} alt="Preview" className={classes.modalImage} />
          </DialogContent>
          <DialogActions>
              <Button 
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = previewImage;
                  link.download = `ia_preview.jpg`;
                  link.target = "_blank";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }} 
                color="secondary"
                startIcon={<GetApp />}
              >
                Descargar Imagen
              </Button>
              <Button onClick={() => setPreviewImage(null)} color="primary">Cerrar</Button>
          </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default AiApprovals;
